import type { VerificationsResponse } from '../../types/identityAPI';

import Environments, { APIs } from '@delta-defense/client-environments';
import React, { useState } from 'react';
import { Redirect } from 'react-router-dom';
import * as z from 'zod/v4';

import EmailChange from '../AdditionalInfo/EmailChange';
import AuthAlert from '../AuthAlert/AuthAlert';
import AuthButton from '../AuthButton/AuthButton';
import { DefaultErrorPage } from '../ErrorPage/defaultErrorPage';
import { InputGroup } from '../InputGroup/InputGroup';
import Loading from '../Loading/Loading';
import DefaultLinks from '../NavigationLinks/DefaultLinks';
import Title from '../Title/Title';

import { getSourceForAuthApi } from '../../utilities/getSourceForAuthApi';
import '../FormContainer/Forms.scss';

const USER_NOT_FOUND_STATUS = 404;

export function ForgotPassword(): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusCode, setStatusCode] = useState<number | null>(null);
  const [verificationID, setVerificationID] = useState<string | null>(null);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setLoading(true);

    const verificationEndpoint = `${Environments.getProxyEndpointForApi(APIs.Auth)}/verifications`;
    const errorMessage = validateEmailAddress(email);
    if (errorMessage) {
      setError(errorMessage);
      setIsEmailVerified(false);
      setLoading(false);

      return;
    }

    const response = await fetch(verificationEndpoint, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, source: getSourceForAuthApi() })
    });
    const body: VerificationsResponse = await response.json();

    setStatusCode(response.status);

    if (response.ok && body.data) {
      setVerificationID(body.data.id);
      setIsEmailVerified(true);
    }

    setLoading(false);
  }

  function validateEmailAddress(email: string): string | void {
    try {
      z.email('Please enter a valid email address').parse(email);
    } catch (error) {
      if (error instanceof z.ZodError) {
        return z.treeifyError(error).errors.join();
      }
    }
  }

  function handleEmailChange(event: React.ChangeEvent<HTMLInputElement>): void {
    setError(null);
    setStatusCode(null);
    setEmail(event.target.value);
  }

  if (isEmailVerified && verificationID) {
    return <Redirect push to={{ pathname: `/forgot-password/${verificationID}` }} />;
  } else if (statusCode && statusCode !== USER_NOT_FOUND_STATUS) {
    return <DefaultErrorPage errorStatus={statusCode} />;
  } else {
    return (
      <>
        <form name='reset-password' onSubmit={handleSubmit}>
          <Title name="Reset Password" />

          <InputGroup
            id='resetPasswordEmail'
            name='email'
            type='email'
            label='Email'
            autoComplete='email'
            value={email}
            onChange={handleEmailChange}
            error={error}
          />

          {statusCode === USER_NOT_FOUND_STATUS && (
            <AuthAlert message="There is no account for the email you entered." />
          )}

          <DefaultLinks />

          <AuthButton
            id="resetPasswordVerificationButton"
            color="btn-gold"
            text={loading ? <Loading /> : 'Send Verification Email'}
            disabled={loading}
          />
        </form>

        <EmailChange />
      </>
    );
  }
}
